import { AYS_INEK_YEARS } from '../inek/years';

/**
 * The code API reports years as strings ("2026") and omits them entirely for
 * some coding systems (OPS answers with `year: null`). Normalise before any
 * comparison — `availableYears.includes('2024')` is silently false.
 */
function toYear(value: number | string | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const year = Number(value);

  return Number.isFinite(year) ? year : undefined;
}

/**
 * Normalise the `availableYears` a code was reported with. `null`, an absent
 * field and an empty list all mean "the backend does not know" and collapse to
 * `undefined` so callers only have to check for one falsy case.
 */
export function normalizeAvailableYears(
  availableYears: (number | string)[] | null | undefined,
): number[] | undefined {
  if (!availableYears?.length) {
    return undefined;
  }

  const years = availableYears
    .map(toYear)
    .filter((year): year is number => year !== undefined);

  return years.length ? [...years].sort((a, b) => a - b) : undefined;
}

/**
 * The years a code browser year select may offer for a code.
 *
 * Falls back to the full InEK year range whenever the backend did not report
 * `availableYears` for that code, which keeps the pre-BDA/ZD-4301 behaviour for
 * every coding system that has not been migrated yet.
 */
export function codeYearOptions(availableYears: number[] | undefined): number[] {
  return availableYears?.length ? [...availableYears] : [...AYS_INEK_YEARS];
}

/**
 * Decide which year a loaded code is actually shown for.
 *
 * The response year wins when the backend reported one, otherwise the year that
 * was requested is kept (OPS answers without a year at all). Either way the
 * result is forced into `availableYears`, so the year select can never end up
 * displaying a year that is not one of its own options.
 */
export function resolveCodeYear(
  responseYear: number | string | null | undefined,
  requestedYear: number | string | null | undefined,
  availableYears: number[] | undefined,
): number {
  const options = codeYearOptions(availableYears);
  const candidates = [toYear(responseYear), toYear(requestedYear)];

  const supported = candidates.find(
    (year) => year !== undefined && options.includes(year),
  );

  if (supported !== undefined) {
    return supported;
  }

  return Math.max(...options);
}
